import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsEmpleado } from '../DatosEmpleado/GoogleMapsEmpleado';

// Icono "casita/base" reutilizado del visor de Siniestros (mismo lenguaje visual).
const ICONO_PUESTO = 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z';

const crearUrlPinSVG = (color) => {
    const pinColor = color || '#1e88e5';
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 24 32">
            <ellipse cx="12" cy="30" rx="6" ry="2" fill="rgba(0,0,0,0.3)"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 18 12 18s12-9 12-18c0-6.627-5.373-12-12-12z" fill="${pinColor}" stroke="#FFFFFF" stroke-width="1.5"/>
            <g transform="translate(4.5, 4.5) scale(0.625)">
                <path d="${ICONO_PUESTO}" fill="#FFFFFF"/>
            </g>
        </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`;
};

const ESTILO_MAPA_LIMPIO = [
    { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] }
];

const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const filaInfo = (label, valor) => {
    if (!valor) return '';
    return `<div style="margin-top:8px;">
                <span style="display:block;font-size:10px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:#94a3b8;">${esc(label)}</span>
                <span style="display:block;color:#334155;font-size:13px;line-height:1.35;">${esc(valor)}</span>
            </div>`;
};

const contenidoInfoWindow = (p) => `
    <div style="font-family:'Inter',sans-serif;width:300px;padding:14px;border-radius:12px;background:#fff;box-shadow:0 4px 12px rgba(0,0,0,0.12);border-left:5px solid ${p.color || '#1e88e5'}">
        <div style="margin-bottom:6px;">
            <span style="background:${(p.color || '#1e88e5')}22;color:${p.color || '#1e88e5'};padding:3px 8px;border-radius:4px;font-weight:700;font-size:10px;letter-spacing:.5px;text-transform:uppercase;">
                <i class="pi pi-building" style="font-size:10px;margin-right:4px;"></i>${esc(p.cliente || 'Puesto')}${p.grupo && p.grupo !== p.cliente ? ' &middot; ' + esc(p.grupo) : ''}
            </span>
        </div>
        <h3 style="margin:2px 0 2px 0;color:#1e293b;font-size:16px;font-weight:600;line-height:1.25;">${esc(p.nombre || 'Puesto')}</h3>
        ${filaInfo('Dirección', p.direccion)}
        ${filaInfo('Referencia de llegada', p.referencia)}
        ${filaInfo('Responsable', p.responsable)}
        ${p.link ? `<div style="margin-top:12px;"><a href="${esc(p.link)}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:6px;text-decoration:none;color:#1a73e8;font-size:13px;font-weight:600;"><i class="pi pi-map-marker"></i> Abrir en Google Maps</a></div>` : ''}
    </div>`;

/**
 * Mapa del directorio de puestos.
 * Props:
 *  - listaDePuntos: [{ id, lat, lng, nombre, cliente, grupo, direccion, referencia, responsable, link, color }]
 *  - selectedPuntoId: al cambiar, hace panTo + zoom + abre el InfoWindow de ese punto (ver uno a uno).
 *  - hoveredPuntoId: rebota el marcador correspondiente.
 */
const MapaDirectorio = ({ listaDePuntos = [], selectedPuntoId, selNonce, hoveredPuntoId }) => {
    const [mapReady, setMapReady] = useState(false);
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const prevDatosRef = useRef('');
    const markersRef = useRef(new Map());
    const overlaysRef = useRef([]);
    const infoWindowRef = useRef(null);

    useEffect(() => {
        const datosActuales = JSON.stringify(listaDePuntos.map((p) => p.id));
        if (prevDatosRef.current === datosActuales && mapRef.current) return;
        prevDatosRef.current = datosActuales;

        loadGoogleMapsEmpleado(() => {
            if (!mapRef.current && mapContainerRef.current) {
                mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
                    center: { lat: -1.8, lng: -79.6 }, // Costa de Ecuador (Guayas / Los Ríos)
                    zoom: 8,
                    styles: ESTILO_MAPA_LIMPIO,
                    mapTypeControl: true,
                    streetViewControl: true,
                    fullscreenControl: true,
                    clickableIcons: false
                });
                setMapReady(true);
            }
            if (!infoWindowRef.current) {
                infoWindowRef.current = new window.google.maps.InfoWindow();
            }

            overlaysRef.current.forEach((o) => o && o.setMap(null));
            overlaysRef.current = [];
            markersRef.current.clear();

            const nuevos = [];
            listaDePuntos.forEach((p) => {
                if (p.lat == null || p.lng == null) return;
                const marker = new window.google.maps.Marker({
                    position: { lat: p.lat, lng: p.lng },
                    title: p.nombre,
                    icon: {
                        url: crearUrlPinSVG(p.color),
                        scaledSize: new window.google.maps.Size(36, 48),
                        anchor: new window.google.maps.Point(18, 32)
                    }
                });
                marker.addListener('click', () => {
                    const currentMap = mapRef.current || marker.getMap();
                    if (currentMap) {
                        infoWindowRef.current.setContent(contenidoInfoWindow(p));
                        infoWindowRef.current.open(currentMap, marker);
                    }
                });
                nuevos.push(marker);
                markersRef.current.set(p.id, marker);
            });

            nuevos.forEach((m) => m.setMap(mapRef.current));
            overlaysRef.current = nuevos;

            if (nuevos.length > 0) {
                const bounds = new window.google.maps.LatLngBounds();
                nuevos.forEach((m) => bounds.extend(m.getPosition()));
                mapRef.current.fitBounds(bounds);
                const listener = window.google.maps.event.addListener(mapRef.current, 'idle', function () {
                    if (mapRef.current.getZoom() > 15) mapRef.current.setZoom(15);
                    window.google.maps.event.removeListener(listener);
                });
            }
        });
    }, [listaDePuntos]);

    // Hover en la tabla -> rebota el marcador
    useEffect(() => {
        if (!mapReady) return;
        markersRef.current.forEach((marker, id) => {
            marker.setAnimation(id === hoveredPuntoId ? window.google.maps.Animation.BOUNCE : null);
        });
    }, [hoveredPuntoId, mapReady]);

    // Selección en la tabla -> vuela al punto y abre su ficha
    useEffect(() => {
        if (!mapReady || selectedPuntoId == null) return;
        const marker = markersRef.current.get(selectedPuntoId);
        if (marker && mapRef.current) {
            mapRef.current.panTo(marker.getPosition());
            mapRef.current.setZoom(16);
            window.google.maps.event.trigger(marker, 'click');
        }
    }, [selectedPuntoId, selNonce, mapReady]);

    return (
        <div className="surface-border border-2 border-round p-1 shadow-2" style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
            <div ref={mapContainerRef} style={{ width: '100%', minHeight: '520px', maxHeight: '80vh', borderRadius: '6px', overflow: 'hidden' }} />
        </div>
    );
};

export default MapaDirectorio;
